import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { systemService } from '../../services/system.service';
import { getErrorMsg } from '../../utils/error';

const AdminEmailPage = () => {
  const { t } = useTranslation('common');

  const [docEmail, setDocEmail] = useState('');
  const [docLoading, setDocLoading] = useState(true);
  const [docSaving, setDocSaving] = useState(false);
  const [docError, setDocError] = useState('');
  const [docSuccess, setDocSuccess] = useState('');

  const [cvEmail, setCvEmail] = useState('');
  const [cvLoading, setCvLoading] = useState(true);
  const [cvSaving, setCvSaving] = useState(false);
  const [cvError, setCvError] = useState('');
  const [cvSuccess, setCvSuccess] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const data = await systemService.getAdminEmail();
        setDocEmail(data.documentNotifyEmail);
      } catch (e) {
        setDocError(getErrorMsg(e, 'Failed to load setting'));
      } finally {
        setDocLoading(false);
      }
    })();
    (async () => {
      try {
        const data = await systemService.getCvNotifyEmail();
        setCvEmail(data.cvNotifyEmail);
      } catch (e) {
        setCvError(getErrorMsg(e, 'Failed to load setting'));
      } finally {
        setCvLoading(false);
      }
    })();
  }, []);

  const handleDocSave = async () => {
    setDocSaving(true);
    setDocError('');
    setDocSuccess('');
    try {
      const data = await systemService.setAdminEmail(docEmail);
      setDocEmail(data.documentNotifyEmail);
      setDocSuccess(t('saved'));
    } catch (e) {
      setDocError(getErrorMsg(e, 'Failed to save'));
    } finally {
      setDocSaving(false);
    }
  };

  const handleCvSave = async () => {
    setCvSaving(true);
    setCvError('');
    setCvSuccess('');
    try {
      const data = await systemService.setCvNotifyEmail(cvEmail);
      setCvEmail(data.cvNotifyEmail);
      setCvSuccess(t('saved'));
    } catch (e) {
      setCvError(getErrorMsg(e, 'Failed to save'));
    } finally {
      setCvSaving(false);
    }
  };

  return (
    <Layout title={t('admin_email_settings')}>
      <div className="space-y-6 max-w-xl">
        {/* Document password notification card */}
        <Card className="p-5 space-y-4">
          {docError && (
            <div className="p-3 rounded-md bg-red-50 text-red-600 border border-red-100">{docError}</div>
          )}
          {docSuccess && (
            <div className="p-3 rounded-md bg-green-50 text-green-600 border border-green-100">{docSuccess}</div>
          )}
          <div>
            <h2 className="text-base font-semibold mb-1">{t('document_notify_email_label')}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {t('document_notify_email_description')}
            </p>
            <Input
              label={t('admin_email')}
              type="email"
              value={docEmail}
              onChange={(e) => setDocEmail(e.target.value)}
              disabled={docLoading}
              placeholder="admin@example.com"
            />
          </div>
          <Button variant="primary" onClick={handleDocSave} disabled={docLoading || docSaving}>
            {docSaving ? t('saving') : t('save')}
          </Button>
        </Card>

        {/* CV submission notification card */}
        <Card className="p-5 space-y-4">
          {cvError && (
            <div className="p-3 rounded-md bg-red-50 text-red-600 border border-red-100">{cvError}</div>
          )}
          {cvSuccess && (
            <div className="p-3 rounded-md bg-green-50 text-green-600 border border-green-100">{cvSuccess}</div>
          )}
          <div>
            <h2 className="text-base font-semibold mb-1">{t('cv_notify_email_label')}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {t('cv_notify_email_description')}
            </p>
            <Input
              label={t('cv_notify_email_heading')}
              type="email"
              value={cvEmail}
              onChange={(e) => setCvEmail(e.target.value)}
              disabled={cvLoading}
              placeholder="admin@example.com"
            />
          </div>
          <Button variant="primary" onClick={handleCvSave} disabled={cvLoading || cvSaving}>
            {cvSaving ? t('saving') : t('save')}
          </Button>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminEmailPage;
